import { useState } from "react";
import { Mail, Send, CheckCircle2, AlertCircle } from "lucide-react";
import Reveal from "@/components/Reveal";
import { HERO_IMAGES, OWNER } from "@/data/site";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus({ state: "error", message: "Please fill in your name, email and message." });
      return;
    }
    const subject = encodeURIComponent(form.subject || "A note for the caretaker");
    const body = encodeURIComponent(
      `From: ${form.name} <${form.email}>\n\n${form.message}\n\n—\nSent from britcars.us`
    );
    const href = `mailto:${OWNER.email}?subject=${subject}&body=${body}`;
    window.location.href = href;
    setStatus({
      state: "success",
      message: `Opening your email program addressed to ${OWNER.email}. If nothing happens, please email ${OWNER.email} directly.`,
    });
  };

  return (
    <div data-testid="contact-page">
      <section className="parchment border-b border-[color:var(--accent)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-12 items-end">
          <Reveal className="md:col-span-7">
            <div className="eyebrow mb-6">Correspondence</div>
            <h1 className="font-display text-6xl md:text-8xl tracking-tighter leading-[0.95]">
              Write to the <span className="italic text-[color:var(--brg)]">caretaker.</span>
            </h1>
            <p className="mt-8 text-lg text-[color:var(--ink-secondary)] max-w-xl">
              For drives, restoration questions, friendly correspondence, or to introduce yourself as a manual‑transmission driver.
            </p>
          </Reveal>
          <Reveal className="md:col-span-5" delay={150}>
            <a
              data-testid="contact-direct-email"
              href={`mailto:${OWNER.email}`}
              className="block group"
            >
              <div className="border border-[color:var(--accent)] p-8 hover:border-[color:var(--brg)] transition">
                <Mail className="text-[color:var(--brg)] mb-4" size={28} />
                <div className="eyebrow mb-2">Direct correspondence</div>
                <div className="font-display text-3xl md:text-4xl group-hover:text-[color:var(--brg)] transition break-all">
                  {OWNER.email}
                </div>
              </div>
            </a>
          </Reveal>
        </div>
      </section>

      <section className="bg-[color:var(--surface)]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28 grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="aspect-[3/4] tile">
              <img src={HERO_IMAGES.detail} alt="Inside the 1950 Morris Minor Tourer — cream steering wheel and ivory dashboard" className="w-full h-full object-cover" />
            </div>
          </Reveal>

          <Reveal className="md:col-span-7" delay={100}>
            <div className="eyebrow mb-4">A few lines</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-10">
              Send a note <span className="italic">below.</span>
            </h2>

            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="field">
                  <label htmlFor="c-name">Name</label>
                  <input
                    id="c-name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-name-input"
                    autoComplete="name"
                    required
                  />
                </div>
                <div className="field">
                  <label htmlFor="c-email">Email</label>
                  <input
                    id="c-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-email-input"
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div className="field">
                <label htmlFor="c-subject">Subject (optional)</label>
                <input
                  id="c-subject"
                  type="text"
                  value={form.subject}
                  onChange={update("subject")}
                  data-testid="contact-subject-input"
                />
              </div>

              <div className="field">
                <label htmlFor="c-message">Message</label>
                <textarea
                  id="c-message"
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-message-input"
                  required
                />
              </div>

              <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  className="btn-primary"
                  data-testid="contact-submit-button"
                >
                  Send Message <Send size={14} />
                </button>

                {status.state === "success" && (
                  <div
                    data-testid="contact-success"
                    className="flex items-start gap-3 text-sm text-[color:var(--brg)] max-w-md"
                  >
                    <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
                    <span>{status.message}</span>
                  </div>
                )}
                {status.state === "error" && (
                  <div
                    data-testid="contact-error"
                    className="flex items-start gap-3 text-sm text-[color:var(--oxblood)] max-w-md"
                  >
                    <AlertCircle size={18} className="mt-0.5 shrink-0" />
                    <span>{status.message}</span>
                  </div>
                )}
              </div>

              <p className="text-xs text-[color:var(--ink-muted)] pt-2">
                Pressing <em>Send Message</em> will open your default email program with your note pre‑filled to {OWNER.email}.
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
